import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Search, Download, Eye, Trash2, ChevronUp, ChevronDown, X, Copy, TrendingUp, Grid, Table, Filter } from 'lucide-react';
import { useScan } from '@/contexts/ScanContext';
import { useAnalytics } from '@/services/analytics';
import { DomainScanResult } from '@/types';
import { AdvancedFilterOptions, BrandRiskAnalysis as BrandRiskData } from '../../../shared/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn, formatDateTime, getStatusColor, getStatusIcon } from '@/lib/utils';
import { getValuationTier } from '@/lib/valuation-utils';
import ValuationBadge from './ValuationBadge';
import DomainValuation from './DomainValuation';
import DomainPricing from './DomainPricing';
import BrandRiskAnalysis from './BrandRiskAnalysis';
import BrandRiskPill from './BrandRiskPill';
import { useBrandRiskQueue } from '@/hooks/useBrandRiskQueue';
import { usePriceChanges } from '@/hooks/usePriceChanges';
import AdvancedFilters from './AdvancedFilters';
import TableSkeleton from './TableSkeleton';

type SortField = 'domain' | 'status' | 'scannedAt' | 'valuation' | 'price';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'grid' | 'table';

const splitDomain = (domain: string) => {
  const idx = domain.indexOf('.');
  if (idx === -1) return { base: domain, tld: '' };
  return { base: domain.slice(0, idx), tld: domain.slice(idx) };
};

interface ResultsTableProps {
  onViewModeChange?: (mode: ViewMode) => void;
  currentViewMode?: ViewMode;
}

const ResultsTable: React.FC<ResultsTableProps> = ({ onViewModeChange, currentViewMode = 'table' }) => {
  const { state, dispatch } = useScan();
  const analytics = useAnalytics();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortField, setSortField] = useState<SortField>('scannedAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilterOptions>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedResult, setSelectedResult] = useState<DomainScanResult | null>(null);
  const [showValuationModal, setShowValuationModal] = useState(false);
  const [valuationResult, setValuationResult] = useState<DomainScanResult | null>(null);
  const [showBrandRiskModal, setShowBrandRiskModal] = useState(false);
  const [brandRiskResult, setBrandRiskResult] = useState<DomainScanResult | null>(null);
  const resultsRef = useRef(state.results);
  useEffect(() => { resultsRef.current = state.results; });

  const onBrandRiskResult = useCallback((domain: string, analysis: BrandRiskData) => {
    const result = resultsRef.current?.find(r => r.domain === domain);
    if (result) {
      const updatedResult = { ...result, brandRisk: analysis };
      dispatch({ type: 'UPDATE_RESULT', payload: updatedResult });
      setBrandRiskResult(prev => prev?.domain === domain ? updatedResult : prev);
    }
  }, [dispatch]);

  const { queue: queueBrandRisk, markVisible, retry: retryBrandRisk, getStatus: getBrandRiskStatus, getResult: getBrandRiskResult } = useBrandRiskQueue(onBrandRiskResult);

  // Price change badges — fetch once the scan is done, for available domains only
  const isDone = !state.isLoading && !state.progress.isScanning;
  const availableDomainsForPriceCheck = useMemo(
    () => (state.results || []).filter(r => r.status === 'available').map(r => r.domain),
    [state.results]
  );
  const priceChanges = usePriceChanges(availableDomainsForPriceCheck, isDone);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (advancedFilters.search) count++;
    if (advancedFilters.regex) count++;
    if (advancedFilters.booleanQuery) count++;
    if (advancedFilters.tlds && advancedFilters.tlds.length > 0) count++;
    if (advancedFilters.lengthMin !== undefined || advancedFilters.lengthMax !== undefined) count++;
    if (advancedFilters.valuationMin !== undefined || advancedFilters.valuationMax !== undefined) count++;
    if (advancedFilters.priceMin !== undefined || advancedFilters.priceMax !== undefined) count++;
    if (advancedFilters.excludeUnavailablePricing) count++;
    if (advancedFilters.premiumOnly) count++;
    if (advancedFilters.purchasableOnly) count++;
    if (advancedFilters.registrar) count++;
    if (advancedFilters.dateFrom || advancedFilters.dateTo) count++;
    return count;
  }, [advancedFilters]);

  // Handle advanced filter changes
  const handleAdvancedFiltersChange = (newFilters: AdvancedFilterOptions) => {
    setAdvancedFilters(newFilters);
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setAdvancedFilters({});
  };

  const filteredResults = useMemo(() => {
    const baseResults = Array.isArray(state.results) ? state.results : [];
    // Work on a shallow copy to avoid mutating context state via sort
    let filtered = [...baseResults];

    if (searchTerm) {
      filtered = filtered.filter(result =>
        result.domain.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(result => result.status === statusFilter);
    }

    // Apply advanced filters
    if (advancedFilters.regex) {
      try {
        const regex = new RegExp(advancedFilters.regex);
        filtered = filtered.filter(result => regex.test(result.domain));
      } catch (error) {
        // Invalid regex, skip this filter
      }
    }

    if (advancedFilters.tlds && advancedFilters.tlds.length > 0) {
      filtered = filtered.filter(result =>
        advancedFilters.tlds!.some((tld: string) => result.domain.endsWith(tld))
      );
    }

    if (advancedFilters.lengthMin !== undefined) {
      filtered = filtered.filter(result => result.domain.length >= advancedFilters.lengthMin!);
    }

    if (advancedFilters.lengthMax !== undefined) {
      filtered = filtered.filter(result => result.domain.length <= advancedFilters.lengthMax!);
    }

    if (advancedFilters.registrar) {
      filtered = filtered.filter(result =>
        result.whoisData?.registrar?.toLowerCase().includes(advancedFilters.registrar!.toLowerCase())
      );
    }

    // Apply pricing filters
    if (advancedFilters.priceMin !== undefined) {
      filtered = filtered.filter(result =>
        result.pricing?.purchasePrice !== null && result.pricing!.purchasePrice! >= advancedFilters.priceMin!
      );
    }

    if (advancedFilters.priceMax !== undefined) {
      filtered = filtered.filter(result =>
        result.pricing?.purchasePrice !== null && result.pricing!.purchasePrice! <= advancedFilters.priceMax!
      );
    }

    if (advancedFilters.excludeUnavailablePricing) {
      filtered = filtered.filter(result =>
        result.pricing?.purchasePrice !== null && result.pricing!.purchasePrice !== undefined
      );
    }

    if (advancedFilters.premiumOnly) {
      filtered = filtered.filter(result =>
        result.pricing?.premium === true
      );
    }

    if (advancedFilters.purchasableOnly) {
      filtered = filtered.filter(result =>
        result.pricing?.purchasable === true
      );
    }

    // Sort results with searched domain always first
    filtered.sort((a, b) => {
      // If we have a searched domain, prioritize it
      if (state.searchedDomain) {
        const aIsSearched = a.domain === state.searchedDomain;
        const bIsSearched = b.domain === state.searchedDomain;

        if (aIsSearched && !bIsSearched) return -1;
        if (!aIsSearched && bIsSearched) return 1;
      }

      // For other domains, apply normal sorting
      let aValue: any;
      let bValue: any;

      if (sortField === 'scannedAt') {
        aValue = new Date(a.scannedAt);
        bValue = new Date(b.scannedAt);
      } else if (sortField === 'valuation') {
        // Sort by minimum estimated value
        aValue = a.valuation?.estimatedValue?.min || 0;
        bValue = b.valuation?.estimatedValue?.min || 0;
      } else if (sortField === 'price') {
        // Sort by purchase price
        aValue = a.pricing?.purchasePrice || 0;
        bValue = b.pricing?.purchasePrice || 0;
      } else {
        // For other fields, use direct property access
        aValue = a[sortField as keyof DomainScanResult];
        bValue = b[sortField as keyof DomainScanResult];
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [state.results, state.searchedDomain, searchTerm, statusFilter, advancedFilters, sortField, sortOrder]);

  const paginatedResults = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredResults.slice(start, start + rowsPerPage);
  }, [filteredResults, page, rowsPerPage]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }

    // Track sorting
    analytics.domainFiltered('sort', `${field}_${sortField === field ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc'}`, filteredResults.length);

    // Track valuation column sorting specifically
    if (field === 'valuation') {
      analytics.valuationColumnSorted(sortField === field ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'asc', filteredResults.length);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDelete = (id: string) => {
    dispatch({ type: 'DELETE_RESULT', payload: id });
  };

  const handleViewDetails = (result: DomainScanResult) => {
    setSelectedResult(result);
    analytics.domainClicked(result.domain, result.status === 'available', paginatedResults.findIndex(r => r.id === result.id));

    // Track valuation view if available
    if (result.valuation) {
      const { tier } = getValuationTier(result.valuation);
      analytics.valuationViewed(result.domain, tier, result.valuation.confidence);
    }
  };

  const handleCopyDomain = async (domain: string, isAvailable: boolean) => {
    try {
      await navigator.clipboard.writeText(domain);
      analytics.domainCopied(domain, isAvailable);
    } catch (err) {
      console.error('Failed to copy domain:', err);
    }
  };

  // Auto-queue available domains for brand risk analysis when results change
  useEffect(() => {
    const results = Array.isArray(state.results) ? state.results : [];
    results.forEach(result => {
      if (result.status === 'available') {
        queueBrandRisk(result.domain);
      }
    });
  }, [state.results, queueBrandRisk]);

  const handleViewBrandRisk = (result: DomainScanResult) => {
    const currentResults = Array.isArray(state.results) ? state.results : [];
    const latestResult = currentResults.find(r => r.id === result.id) || result;
    setBrandRiskResult(latestResult);
    setShowBrandRiskModal(true);
  };

  const exportResults = (format: 'csv' | 'json') => {
    const data = filteredResults.map(result => ({
      domain: result.domain,
      status: result.status,
      registrar: result.whoisData?.registrar || '',
      expirationDate: result.whoisData?.expirationDate || '',
      ipAddresses: Array.isArray(result.dnsData?.ipAddresses) ? result.dnsData.ipAddresses.join(', ') : '',
      scannedAt: result.scannedAt,
    }));

    // Track export
    analytics.featureUsed('export_results', {
      format,
      results_count: data.length
    });

    if (format === 'csv') {
      const csvContent = [
        'Domain,Status,Registrar,Expiration Date,IP Addresses,Scanned At',
        ...data.map(row => Object.values(row).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `domain-scan-results-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `domain-scan-results-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  if ((state.results || []).length === 0 && !state.isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-slate-400 dark:text-slate-600 mb-4">
            <Search className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-slate-900 dark:text-white mb-2">
            No scan results yet
          </h3>
          <p className="text-slate-600 dark:text-slate-400">
            Start by scanning a domain using the input on the left
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with controls */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Results ({filteredResults.length})
            </h3>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportResults('csv')}
                className="h-8 px-3 text-xs"
              >
                <Download className="h-3 w-3 mr-1" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportResults('json')}
                className="h-8 px-3 text-xs"
              >
                <Download className="h-3 w-3 mr-1" />
                JSON
              </Button>
            </div>
          </div>

          {/* View Mode Toggle */}
          {onViewModeChange && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">View:</span>
              <div className="flex border border-slate-200 dark:border-slate-700 rounded-lg">
                <Button
                  variant={currentViewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onViewModeChange('grid')}
                  className="h-8 px-3 rounded-r-none"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={currentViewMode === 'table' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => onViewModeChange('table')}
                  className="h-8 px-3 rounded-l-none"
                >
                  <Table className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search domains..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                if (e.target.value) {
                  analytics.domainFiltered('search', e.target.value, filteredResults.length);
                }
              }}
              className="pl-10 h-9 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(true)}
              className="flex items-center space-x-2 h-9"
            >
              <Filter className="h-4 w-4" />
              <span>Advanced</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                analytics.domainFiltered('status', e.target.value, filteredResults.length);
              }}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 h-9 text-sm"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="registered">Registered</option>
              <option value="error">Error</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-x-auto overflow-y-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('domain')}
                    className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <span>Domain</span>
                    {sortField === 'domain' && (
                      sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <span>Status</span>
                    {sortField === 'status' && (
                      sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                  <button
                    onClick={() => handleSort('valuation')}
                    className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <span>Estimated Price</span>
                    {sortField === 'valuation' && (
                      sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                  Brand Risk
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Purchase Price
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  Price Trend
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  Registrar
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  Expiration
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">
                  IP Addresses
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                  <button
                    onClick={() => handleSort('scannedAt')}
                    className="flex items-center space-x-1 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <span>Scanned At</span>
                    {sortField === 'scannedAt' && (
                      sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Show skeleton rows when scanning but no results yet */}
              {state.isLoading && (state.results || []).length === 0 ? (
                <TableSkeleton rows={rowsPerPage} />
              ) : (
                paginatedResults.map((result) => {
                  const isSearchedDomain = state.searchedDomain && result.domain === state.searchedDomain;
                  const { base, tld } = splitDomain(result.domain);
                  return (
                    <tr
                      key={result.id}
                      className={cn(
                        "group hover:bg-gray-50 dark:hover:bg-gray-800",
                        isSearchedDomain && "bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500"
                      )}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className={cn(
                            "text-sm font-medium",
                            isSearchedDomain
                              ? "text-blue-900 dark:text-blue-100 font-semibold"
                              : "text-gray-900 dark:text-white"
                          )}>
                            <span className="text-gray-400 font-medium">{base}</span>
                            <span className="font-bold text-gray-900">{tld}</span>
                          </div>
                          {isSearchedDomain && (
                            <div className="px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs font-medium rounded-full">
                              Searched
                            </div>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyDomain(result.domain, result.status === 'available')}
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {result.status === 'available' ? (
                        <span className="text-gray-900 text-sm">Available</span>
                      ) : result.status === 'registered' ? (
                        <span className="text-gray-400 text-sm">Taken</span>
                      ) : (
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-xs font-medium px-2 py-1",
                            getStatusColor(result.status)
                          )}
                        >
                          <span className="mr-1">{getStatusIcon(result.status)}</span>
                          {result.status.charAt(0).toUpperCase() + result.status.slice(1)}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                      <ValuationBadge
                        valuation={result.valuation}
                        compact={true}
                        showConfidence={false}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                      <BrandRiskPill
                        domain={result.domain}
                        isAvailable={result.status === 'available'}
                        status={getBrandRiskStatus(result.domain)}
                        result={getBrandRiskResult(result.domain)}
                        onVisible={markVisible}
                        onViewRisk={() => handleViewBrandRisk(result)}
                        onRetry={retryBrandRisk}
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <DomainPricing pricing={result.pricing} size="sm" />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap hidden sm:table-cell">
                      {priceChanges.get(result.domain)?.changeLabel ? (
                        <span className={`text-xs font-medium ${
                          (priceChanges.get(result.domain)?.changePercent ?? 0) < 0
                            ? 'text-green-600'
                            : 'text-red-500'
                        }`}>
                          {priceChanges.get(result.domain)?.changeLabel}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate hidden sm:table-cell">
                      {result.whoisData?.registrar || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                      {result.whoisData?.expirationDate
                        ? formatDateTime(result.whoisData.expirationDate)
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate hidden lg:table-cell">
                      {Array.isArray(result.dnsData?.ipAddresses) ? result.dnsData.ipAddresses.join(', ') : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                      {formatDateTime(result.scannedAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(result)}
                          className="h-7 w-7 p-0"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        {/* New Valuation Modal Trigger (icon button) */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Track valuation view
                            if (result.valuation) {
                              const { tier } = getValuationTier(result.valuation);
                              analytics.valuationViewed(result.domain, tier, result.valuation.confidence);
                            }
                            setValuationResult(result);
                            setShowValuationModal(true);
                          }}
                          className="h-7 w-7 p-0"
                          title="View Valuation"
                          aria-label="View Valuation"
                        >
                          <TrendingUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(result.id)}
                          className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-700 dark:text-gray-300">
            Rows per page:
          </span>
          <select
            value={rowsPerPage}
            onChange={(e) => handleChangeRowsPerPage(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-700 dark:text-gray-300">
            {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredResults.length)} of {filteredResults.length}
          </span>
          <div className="flex space-x-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleChangePage(page - 1)}
              disabled={page === 0}
              className="h-7 px-2 text-xs"
            >
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleChangePage(page + 1)}
              disabled={page >= Math.ceil(filteredResults.length / rowsPerPage) - 1}
              className="h-7 px-2 text-xs"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {selectedResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Domain Details</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedResult(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">{selectedResult.domain}</h3>
                {selectedResult.error && (
                  <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                    <span className="text-red-800 dark:text-red-200 text-sm">{selectedResult.error}</span>
                  </div>
                )}
              </div>

              {selectedResult.whoisData && (
                <div>
                  <h4 className="font-semibold mb-3">WHOIS Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Registrar</label>
                      <p className="text-sm text-gray-900 dark:text-white">{selectedResult.whoisData.registrar || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Creation Date</label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedResult.whoisData.creationDate
                          ? formatDateTime(selectedResult.whoisData.creationDate)
                          : 'N/A'
                        }
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Expiration Date</label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedResult.whoisData.expirationDate
                          ? formatDateTime(selectedResult.whoisData.expirationDate)
                          : 'N/A'
                        }
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {selectedResult.whoisData.lastUpdated
                          ? formatDateTime(selectedResult.whoisData.lastUpdated)
                          : 'N/A'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedResult.dnsData && (
                <div>
                  <h4 className="font-semibold mb-3">DNS Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">IP Addresses</label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {(() => {
                          const ipAddresses = selectedResult.dnsData?.ipAddresses;
                          if (Array.isArray(ipAddresses)) {
                            return ipAddresses.join(', ') || 'N/A';
                          }
                          return 'N/A';
                        })()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Name Servers</label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {(() => {
                          const nameServers = selectedResult.whoisData?.nameServers;
                          if (Array.isArray(nameServers)) {
                            return nameServers.join(', ') || 'N/A';
                          } else if (typeof nameServers === 'string') {
                            return nameServers || 'N/A';
                          }
                          return 'N/A';
                        })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Domain Valuation moved to standalone modal */}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Standalone Valuation Modal */}
      {showValuationModal && valuationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Smart Valuation</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowValuationModal(false);
                    setValuationResult(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DomainValuation
                valuation={valuationResult.valuation}
                domain={valuationResult.domain}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Brand Risk Modal */}
      {showBrandRiskModal && brandRiskResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Brand Risk Analysis</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowBrandRiskModal(false);
                    setBrandRiskResult(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <BrandRiskAnalysis
                key={`${brandRiskResult.domain}-${brandRiskResult.brandRisk?.analyzedAt || 'no-analysis'}`}
                brandRisk={brandRiskResult.brandRisk}
                domain={brandRiskResult.domain}
                onAnalysisComplete={(analysis) => {
                  const updatedResult = { ...brandRiskResult, brandRisk: analysis };
                  setBrandRiskResult(updatedResult);
                  dispatch({ type: 'UPDATE_RESULT', payload: updatedResult });
                }}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Advanced Filters</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAdvancedFilters(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AdvancedFilters
                filters={advancedFilters}
                onFiltersChange={handleAdvancedFiltersChange}
                onClearFilters={handleClearAllFilters}
              />
              <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowAdvancedFilters(false)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => setShowAdvancedFilters(false)}
                >
                  Apply Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ResultsTable;